import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { ButtonsTagsPositionsF11 } from './ButtonsTagsPositionsF11';
import { ButtonsTagsPositionsF5 } from './ButtonsTagsPositionsF5';

import {
  Button,
  Dropdown,
  Form,
  InputCheckbox,
  InputSlider,
  Label,
} from '@/components';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useNormalizeInitialF5 } from '@/hooks/useNormalizeInitialF5';
import { useTeamStore } from '@/hooks/useTeamStore';
import ROUTES from '@/router/routes';
import { TeamActionTypes } from '@/store/team';
import { SelectValue, Maybe, PlayerPositionAbbr } from '@/types';
import { EnumTeamContext } from '@/types/team';
import {
  MAX_INPUT_RANGE,
  MIN_INPUT_RANGE,
  ORDER_FILTER_LIST,
  POSITIONS_LABELS,
  areArrayEqual,
  cn,
} from '@/utils';
import { fieldPlayersF5 } from '@/utils/team';

export type StateFilter = {
  hasFilterApplied: boolean;
  filters: {
    orderBy: Maybe<SelectValue>;
    orderClub: Maybe<SelectValue>;
    intoBudget: boolean;
    valuesRange: number[];
    position?: PlayerPositionAbbr | null;
    positionF5?: PlayerPositionAbbr[] | null;
  };
};

export const FormFilterPlayers = ({
  context,
}: {
  context?: EnumTeamContext;
}) => {
  const { userInitialData } = useAuth();
  const { state, dispatch } = useTeamStore();
  const { isMobile } = useIsDesktop();
  const { normalizeInitialF5 } = useNormalizeInitialF5();

  const isF5 =
    context &&
    [
      EnumTeamContext.Build5,
      EnumTeamContext.Team5,
      EnumTeamContext.FilterTeam5,
    ].includes(context);

  const location = useLocation();
  const isBuildF5 = location.pathname.includes(ROUTES.buildTeam5);

  const ButtonsTagsPositions = isBuildF5
    ? ButtonsTagsPositionsF5
    : ButtonsTagsPositionsF11;

  const teams = useMemo(() => {
    return (
      (userInitialData &&
        userInitialData?.equipos_torneo.map((team): SelectValue => {
          return {
            label: team.nombre,
            value: team.id,
          };
        })) ||
      []
    );
  }, [userInitialData]);

  const listTeams = useMemo(() => {
    return [{ label: 'Todos los clubes', value: -2 }, ...teams];
  }, [teams]);

  const initialStateFilter: StateFilter = useMemo(() => {
    return {
      hasFilterApplied: false,
      filters: {
        orderBy: state.filtersApplied?.orderBy || ORDER_FILTER_LIST[0],
        orderClub: state.filtersApplied?.orderClub || listTeams[0],
        intoBudget: state.filtersApplied?.intoBudget || false,
        valuesRange: state.filtersApplied?.valuesRange || [
          MIN_INPUT_RANGE,
          MAX_INPUT_RANGE,
        ],
        position:
          state.filtersApplied?.position ||
          (state.selectedPosition as PlayerPositionAbbr),
        positionF5: normalizeInitialF5,
      },
    };
  }, [
    listTeams,
    normalizeInitialF5,
    state.filtersApplied?.intoBudget,
    state.filtersApplied?.orderBy,
    state.filtersApplied?.orderClub,
    state.filtersApplied?.position,
    state.filtersApplied?.valuesRange,
    state.selectedPosition,
  ]);

  const [stateFilter, setStateFilter] = useState(initialStateFilter);

  const handleOrderBy = (selected: SelectValue) => {
    setStateFilter({
      ...stateFilter,
      filters: { ...stateFilter.filters, orderBy: selected },
    });
  };

  const handleOrderClub = (selected: SelectValue) => {
    setStateFilter({
      ...stateFilter,
      filters: { ...stateFilter.filters, orderClub: selected },
    });
  };

  const handleRangePrice = (price: number[]) => {
    setStateFilter({
      ...stateFilter,
      filters: { ...stateFilter.filters, valuesRange: price },
    });
  };

  const handleBudget = (isChecked: boolean) => {
    setStateFilter({
      ...stateFilter,
      filters: {
        ...stateFilter.filters,
        intoBudget: isChecked,
      },
    });
  };

  const handlePosition = (
    position: Maybe<PlayerPositionAbbr | PlayerPositionAbbr[]>
  ) => {
    setStateFilter({
      ...stateFilter,
      filters: {
        ...stateFilter.filters,
        position: position as PlayerPositionAbbr,
        positionF5: position as PlayerPositionAbbr[],
      },
    });
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (state.filtersApplied?.intoBudget) {
        handleBudget(state.filtersApplied?.intoBudget);
      }
    }, 0);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.balance]);

  const getSelectedPosition = useMemo(() => {
    if (
      !stateFilter.filters.position &&
      userInitialData?.tiene_equipo &&
      !isBuildF5
    )
      return stateFilter.filters.position;

    if (state.selectedPosition !== POSITIONS_LABELS.all_field_players) {
      return stateFilter.filters.position
        ? stateFilter.filters.position
        : state.selectedPosition;
    }
  }, [
    isBuildF5,
    state.selectedPosition,
    stateFilter.filters.position,
    userInitialData?.tiene_equipo,
  ]);

  const resetPositionF5 = useMemo(() => {
    if (
      state.selectedPosition === POSITIONS_LABELS.abbreviation.ARQ ||
      stateFilter.filters.positionF5?.includes(
        POSITIONS_LABELS.abbreviation.ARQ
      )
    ) {
      return null;
    }

    if (
      !stateFilter.filters.positionF5?.includes(
        POSITIONS_LABELS.abbreviation.ARQ
      )
    ) {
      return state.selectedPlayerOnBuildTeam || state.selectedPlayerOnTeam
        ? fieldPlayersF5
        : stateFilter.filters.position || state.selectedPosition;
    }
    return null;
  }, [
    state.selectedPlayerOnBuildTeam,
    state.selectedPlayerOnTeam,
    state.selectedPosition,
    stateFilter.filters.position,
    stateFilter.filters.positionF5,
  ]);

  const resetFilters = () => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        selectedPosition: getSelectedPosition,
        showFiltersPlayersList: false,
        filtersApplied: {
          orderBy: null,
          orderClub: null,
          intoBudget: false,
          valuesRange: null,
          position: null,
          positionF5: resetPositionF5 as PlayerPositionAbbr[],
        },
      },
    });
    setStateFilter(initialStateFilter);
  };

  const getSelectedPositionF5 = useMemo(() => {
    if (!stateFilter.filters.positionF5 && !userInitialData?.tiene_equipo_f5)
      return stateFilter.filters.positionF5;

    if (isMobile) {
      return stateFilter.filters.positionF5 || fieldPlayersF5;
    } else {
      if (state.selectedPosition === POSITIONS_LABELS.abbreviation.ARQ) {
        if (state.selectedPlayerOnBuildTeam || state.selectedPlayerOnTeam) {
          return stateFilter.filters.positionF5 || state.selectedPosition;
        } else {
          return stateFilter.filters.position || state.selectedPosition;
        }
      } else {
        if (state.selectedPlayerOnBuildTeam || state.selectedPlayerOnTeam) {
          return stateFilter.filters.positionF5 || fieldPlayersF5;
        } else {
          return stateFilter.filters.position || state.selectedPosition;
        }
      }
    }
  }, [
    isMobile,
    state.selectedPlayerOnBuildTeam,
    state.selectedPlayerOnTeam,
    state.selectedPosition,
    stateFilter.filters.position,
    stateFilter.filters.positionF5,
    userInitialData?.tiene_equipo_f5,
  ]);

  const handleSubmit = () => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        showFiltersPlayersList: false,
        selectedPosition: getSelectedPosition,
        filtersApplied: {
          orderBy:
            stateFilter.filters.orderBy && stateFilter.filters.orderBy.value > 0
              ? stateFilter.filters.orderBy
              : null,
          orderClub:
            stateFilter.filters.orderClub &&
            stateFilter.filters.orderClub.value >= 0
              ? stateFilter.filters.orderClub
              : null,
          intoBudget: stateFilter.filters.intoBudget,
          valuesRange: stateFilter.filters.valuesRange,
          position: getSelectedPosition,
          positionF5: getSelectedPositionF5 as PlayerPositionAbbr[],
        },
      },
    });
  };

  const hasFilterAppliedMemo = useMemo(() => {
    return (
      stateFilter.filters.intoBudget ||
      (stateFilter.filters.orderClub &&
        stateFilter.filters.orderClub?.value >= 0) ||
      (stateFilter.filters.positionF5 &&
        state.filtersApplied?.positionF5 &&
        !areArrayEqual(
          stateFilter.filters.positionF5,
          state.filtersApplied?.positionF5
        )) ||
      (stateFilter.filters.orderBy && stateFilter.filters.orderBy?.value > 0) ||
      (!isBuildF5 &&
        stateFilter.filters?.position !== state.selectedPosition) ||
      (state.filtersApplied?.valuesRange &&
        !areArrayEqual(
          stateFilter.filters.valuesRange,
          state.filtersApplied?.valuesRange
        ))
    );
  }, [
    stateFilter,
    state.filtersApplied?.positionF5,
    state.filtersApplied?.valuesRange,
    state.selectedPosition,
    isBuildF5,
  ]);

  useEffect(() => {
    if (stateFilter.hasFilterApplied !== Boolean(hasFilterAppliedMemo)) {
      setStateFilter({
        ...stateFilter,
        hasFilterApplied: Boolean(hasFilterAppliedMemo),
      });
    }
  }, [hasFilterAppliedMemo, stateFilter, stateFilter.hasFilterApplied]);

  return (
    <section
      className={cn('flex flex-col flex-1 bg-gdt-gray-7 overflow-auto', {
        'lg:h-[calc(100%+280px)] h-full': state.showPlayersList,
        'lg:h-[calc(100%-1px)] h-full': isF5,
      })}
    >
      <div className="p-6 flex-1 lg:flex-none">
        <Form.Form
          className="space-y-5"
          loading={false}
          onSubmit={handleSubmit}
        >
          <Form.Field className="w-full">
            <Dropdown
              label="Ordenar por"
              options={ORDER_FILTER_LIST}
              optionsClassName="max-h-[263px]"
              selected={stateFilter.filters?.orderBy || ORDER_FILTER_LIST[0]}
              onChange={(selected) => handleOrderBy(selected)}
            />
          </Form.Field>
          <Form.Field className="w-full space-y-[10px]">
            <Label>Posiciones</Label>
            <ButtonsTagsPositions
              positionByFilter={
                isBuildF5
                  ? stateFilter.filters.positionF5
                  : stateFilter.filters.position
              }
              onSubmit={handlePosition}
            />
          </Form.Field>
          <Form.Field className="w-full">
            <Dropdown
              label="Club"
              options={listTeams}
              selected={stateFilter.filters?.orderClub || listTeams[0]}
              onChange={(selected) => handleOrderClub(selected)}
            />
          </Form.Field>
          <Form.Field className="w-full">
            <Label>Cotización</Label>
            <div className="max-w-[92.5%] m-auto wrapper--input-range ">
              <InputSlider
                maxInputRangeAmmount={MAX_INPUT_RANGE}
                minInputRangeAmmount={MIN_INPUT_RANGE}
                step={100000}
                values={stateFilter.filters.valuesRange}
                onChange={(newValue) => handleRangePrice(newValue)}
              />
              <div className="flex justify-between w-[107%] -left-[3.5%] relative items-center text-xs text-gdt-gray-4 top-[-5px]">
                <span>Mín.</span>
                <span>Máx.</span>
              </div>
            </div>
          </Form.Field>
          {!isF5 && (
            <Form.Field className="w-full">
              <InputCheckbox
                className="text-xs lg:text-sm"
                isChecked={stateFilter.filters.intoBudget}
                label="Dentro del presupuesto"
                name="intoABudget"
                onChange={(name, checked) => {
                  handleBudget(checked);
                }}
              />
            </Form.Field>
          )}
        </Form.Form>
      </div>
      <div className="flex space-x-[6px] justify-around mt-5 mb-5 px-6">
        <Button
          className="w-full"
          disabled={!stateFilter.hasFilterApplied}
          type="button"
          variant="secondary"
          onClick={resetFilters}
        >
          Borrar filtros
        </Button>
        <Button
          className="w-full"
          disabled={!stateFilter.hasFilterApplied}
          type="button"
          variant="primary"
          onClick={handleSubmit}
        >
          Aplicar
        </Button>
      </div>
    </section>
  );
};
